import { useEffect, useMemo, useRef } from 'react';
import { Animated, Dimensions, Easing, Platform, Pressable, StyleSheet, Text, View } from 'react-native';
import { useTranslation } from 'react-i18next';

import { levelColor, levelTierKey, type LevelInfo } from '@/components/ui/LevelBadge';
import { fontSize, radius, spacing } from '@/constants/theme';
import type { AppColors } from '@/constants/theme';
import { useTheme } from '@/contexts/ThemeContext';

const { width: W, height: H } = Dimensions.get('window');

const CONFETTI_COUNT = 30;
const CONFETTI_COLORS = ['#F59E0B', '#3B82F6', '#10B981', '#EF4444', '#8B5CF6', '#EC4899'];

type ConfettiPiece = {
  x: number;
  color: string;
  delay: number;
  size: number;
};

function generateConfetti(): ConfettiPiece[] {
  return Array.from({ length: CONFETTI_COUNT }, (_, i) => ({
    x: Math.random() * W,
    color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
    delay: Math.random() * 600,
    size: 6 + Math.random() * 8,
  }));
}

function ConfettiBit({ piece }: { piece: ConfettiPiece }) {
  const y = useRef(new Animated.Value(-20)).current;
  const rotate = useRef(new Animated.Value(0)).current;
  const opacity = useRef(new Animated.Value(1)).current;

  useEffect(() => {
    Animated.parallel([
      Animated.sequence([
        Animated.delay(piece.delay),
        Animated.timing(y, {
          toValue: H + 20,
          duration: 1800,
          easing: Easing.out(Easing.quad),
          useNativeDriver: Platform.OS !== 'web',
        }),
      ]),
      Animated.sequence([
        Animated.delay(piece.delay),
        Animated.loop(
          Animated.timing(rotate, { toValue: 1, duration: 600, useNativeDriver: Platform.OS !== 'web' }),
        ),
      ]),
      Animated.sequence([
        Animated.delay(piece.delay + 1200),
        Animated.timing(opacity, { toValue: 0, duration: 600, useNativeDriver: Platform.OS !== 'web' }),
      ]),
    ]).start();
  // piece is stable (generated once via useRef)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const rotateStr = rotate.interpolate({ inputRange: [0, 1], outputRange: ['0deg', '360deg'] });

  return (
    <Animated.View
      pointerEvents="none"
      style={{
        position: 'absolute',
        top: 0,
        left: piece.x,
        width: piece.size,
        height: piece.size,
        backgroundColor: piece.color,
        borderRadius: piece.size * 0.2,
        transform: [{ translateY: y }, { rotate: rotateStr }],
        opacity,
      }}
    />
  );
}

type Props = {
  info: LevelInfo;
  onDone: () => void;
};

export function LevelUpOverlay({ info, onDone }: Props) {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const styles = useMemo(() => makeStyles(colors), [colors]);
  const confetti = useRef(generateConfetti()).current;
  const color = levelColor(info.level);
  const fadeAnim = useRef(new Animated.Value(0)).current;
  const cardFade = useRef(new Animated.Value(0)).current;
  const closing = useRef(false);

  function handleClose() {
    if (closing.current) return;
    closing.current = true;
    Animated.timing(fadeAnim, { toValue: 0, duration: 400, useNativeDriver: Platform.OS !== 'web' }).start(() => onDone());
  }

  useEffect(() => {
    Animated.timing(fadeAnim, { toValue: 1, duration: 300, useNativeDriver: Platform.OS !== 'web' }).start();
    Animated.sequence([
      Animated.delay(100),
      Animated.timing(cardFade, { toValue: 1, duration: 400, useNativeDriver: Platform.OS !== 'web' }),
    ]).start();
    const timer = setTimeout(handleClose, 4000);
    return () => clearTimeout(timer);
  // handleClose reads a ref — safe to omit
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Animated.View
      style={[StyleSheet.absoluteFill, { opacity: fadeAnim }]}
      pointerEvents="box-none"
    >
      {confetti.map((p, i) => (
        <ConfettiBit key={i} piece={p} />
      ))}

      <Pressable style={styles.backdrop} onPress={handleClose}>
        <Animated.View style={[styles.card, { borderColor: color, opacity: cardFade }]}>
          <Text style={styles.emoji}>🎉</Text>
          <Text style={[styles.levelText, { color }]}>{t('levelUp.levelLabel', { level: info.level })}</Text>
          <Text style={[styles.titleText, { color }]}>{t(`levelBadge.${levelTierKey(info.level)}`)}</Text>
          <Text style={styles.bodyText}>
            {t('levelUp.body')}
          </Text>
          <Pressable style={[styles.btn, { backgroundColor: color }]} onPress={handleClose}>
            <Text style={styles.btnText}>{t('levelUp.continue')}</Text>
          </Pressable>
        </Animated.View>
      </Pressable>
    </Animated.View>
  );
}

function makeStyles(colors: AppColors) {
  return StyleSheet.create({
    backdrop: {
      flex: 1,
      backgroundColor: 'rgba(0,0,0,0.55)',
      alignItems: 'center',
      justifyContent: 'center',
      padding: spacing.xl,
    },
    card: {
      backgroundColor: colors.surface,
      borderRadius: radius.xl,
      borderWidth: 2,
      padding: spacing.xl,
      alignItems: 'center',
      gap: spacing.sm,
      width: '100%',
      maxWidth: 320,
    },
    emoji: {
      fontSize: 52,
    },
    levelText: {
      fontSize: 36,
      fontWeight: '800',
      letterSpacing: 1,
    },
    titleText: {
      fontSize: fontSize.xl,
      fontWeight: '700',
    },
    bodyText: {
      color: colors.textMuted,
      fontSize: fontSize.md,
      textAlign: 'center',
      lineHeight: 22,
      marginTop: spacing.xs,
    },
    btn: {
      marginTop: spacing.sm,
      borderRadius: radius.md,
      paddingVertical: spacing.md,
      paddingHorizontal: spacing.xl,
    },
    btnText: {
      color: '#fff',
      fontSize: fontSize.md,
      fontWeight: '700',
    },
  });
}
